import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "project-team" }
const _hoisted_2 = { class: "team-header" }
const _hoisted_3 = { class: "team-title" }
const _hoisted_4 = { class: "team-actions" }
const _hoisted_5 = {
  key: 0,
  class: "team-table"
}
const _hoisted_6 = { class: "team-list-header" }
const _hoisted_7 = { class: "header" }
const _hoisted_8 = { class: "avatar" }
const _hoisted_9 = { class: "fullname" }
const _hoisted_10 = { class: "role" }
const _hoisted_11 = { class: "hourlyRate" }
const _hoisted_12 = { class: "active" }
const _hoisted_13 = {
  key: 1,
  class: "empty"
}

import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import ButtonIcon from "@/components/ButtonIcon.vue";
import EntitiesList from "@/components/list/EntitiesList.vue";

import AddProjectTeamMember from "./AddProjectTeamMember.vue";
import EditProjectTeamMember from "./EditProjectTeamMember.vue";

import { GET_PROJECT_TEAM } from "@/graphql/project/queries";

import enUK from "@/locales/project/en-UK.json";
import { formatPrice } from "@/helpers/utils";
import { TeamMember } from "@/helpers/project/typesDefinition";

interface TeamMemberRow {
  id: string;
  avatar: {
    name: string;
    avatarUrl?: string;
  };
  fullname: string;
  role: string;
  hourlyRate: number;
  active: boolean;
  raw: TeamMember;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectTeam',
  props: {
  projectId: {
    type: String,
    required: true,
  },
},
  emits: ["teamUpdated"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

provide("columnsOrder", {
  avatar: null,
  fullname: null,
  role: null,
  hourlyRate: null,
  active: null,
});

const { result, onError, refetch } = useQuery(GET_PROJECT_TEAM, () => ({
  id: props.projectId,
}));

const rawTeamMembers = computed<TeamMember[]>(() => {
  const team = result.value?.project?.team ?? [];
  console.log(
    "[ProjectTeam] rawTeamMembers:",
    team.length,
    team.map((t) => `${t.user.firstName} ${t.user.lastName}`),
  );
  return team;
});

const teamMembers = computed<TeamMemberRow[]>(() => {
  return rawTeamMembers.value.map((teamMember) => {
    const fullName = [teamMember.user.firstName, teamMember.user.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      id: `${teamMember.user.id}-${teamMember.projectRole.id}`,
      avatar: {
        name: fullName,
        avatarUrl: teamMember.user.avatarUrl,
      },
      fullname: fullName,
      role: teamMember.projectRole.name,
      hourlyRate: teamMember.projectRole.hourlyRate,
      active: teamMember.active,
      raw: teamMember,
    };
  });
});

const isAddModalVisible = ref(false);
const isEditModalVisible = ref(false);
const selectedTeamMember = ref<TeamMember | null>(null);

function formatAvatar(value: { name: string; avatarUrl?: string }): {
  name: string;
  avatarUrl?: string;
} {
  return value;
}

function formatHourlyRate(value: number): string {
  return formatPrice(value);
}

function formatActive(value: boolean): string {
  return value ? t("labels.yes") : t("labels.no");
}

const formatters = {
  avatar: formatAvatar,
  hourlyRate: formatHourlyRate,
  active: formatActive,
};

function showAddModal(): void {
  isAddModalVisible.value = true;
}

function closeAddModal(): void {
  isAddModalVisible.value = false;
}

function handleTeamMemberClick(teamMember: TeamMemberRow): void {
  console.log("Clicked team member row", teamMember);
  console.log("Clicked team member raw", teamMember.raw);

  selectedTeamMember.value = teamMember.raw;
  isEditModalVisible.value = true;
}

function closeEditModal(): void {
  isEditModalVisible.value = false;
  selectedTeamMember.value = null;
}

function handleTeamUpdated(): void {
  refetch();
  emit("teamUpdated");
}

onError((error) => {
  logErrorMessages(error);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(t)("labels.teamManagement")), 1),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(ButtonIcon, {
          label: _unref(t)('buttons.addTeamMember'),
          "icon-name": "material-symbols:add",
          onClick: showAddModal
        }, null, 8, ["label"])
      ])
    ]),
    (teamMembers.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("ul", _hoisted_6, [
            _createElementVNode("li", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("labels.avatar")), 1),
              _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(t)("labels.fullname")), 1),
              _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)("labels.role")), 1),
              _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)("labels.hourlyRate")), 1),
              _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(t)("labels.active")), 1)
            ])
          ]),
          _createVNode(EntitiesList, {
            elements: teamMembers.value,
            formatters: formatters,
            "avatar-column": "avatar",
            onGoEntityDetails: handleTeamMemberClick
          }, null, 8, ["elements"])
        ]))
      : (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(t)("empty.team")), 1)),
    (isAddModalVisible.value)
      ? (_openBlock(), _createBlock(AddProjectTeamMember, {
          key: 2,
          "project-id": __props.projectId,
          "current-team-members": rawTeamMembers.value,
          onClose: closeAddModal,
          onAdded: handleTeamUpdated
        }, null, 8, ["project-id", "current-team-members"]))
      : _createCommentVNode("", true),
    (isEditModalVisible.value && selectedTeamMember.value)
      ? (_openBlock(), _createBlock(EditProjectTeamMember, {
          key: 3,
          "project-id": __props.projectId,
          "team-member": selectedTeamMember.value,
          onClose: closeEditModal,
          onUpdated: handleTeamUpdated
        }, null, 8, ["project-id", "team-member"]))
      : _createCommentVNode("", true)
  ]))
}
}

})